import Collapsable from 'site/components/ui/Collapsable.tsx'
import Icon from 'site/components/ui/Icon.tsx'
import RenderHTML from 'site/components/ui/RenderHTML.tsx'
import { useSignal } from '@preact/signals'

interface Props {
    text: string
    /**
     * @title Variação de layout da descrição
     * @description Escolha o layout da descrição do produto
     * @default default
     */
    variant?: 'default' | 'alt'
    /**
     * @title Alt: Altura inicial (px)
     * @description Altura máxima antes de exibir o botão "Ver mais" no layout alternativo
     * @default 160
     */
    altInitialMaxHeight?: number
}

function DefaultVariant({ text }: { text: string }) {
    return (
        <Collapsable
            title={
                <div class='flex items-center justify-between text-base text-neutral-0 pb-3 border-b border-b-neutral-4'>
                    <h2>
                        <strong class='font-semibold text-sm text-neutral-1'>
                            Descrição do Produto
                        </strong>
                    </h2>
                    <Icon
                        id='CollapsableArrow'
                        width={11}
                        height={6}
                        class='group-open:rotate-180 transition-transform'
                    />
                </div>
            }
        >
            <RenderHTML
                html={text}
                class='prose font-tt-norms text-neutral-1 leading-tight text-sm mt-3'
            />
        </Collapsable>
    )
}

function AltVariant(
    { text, altInitialMaxHeight = 112 }: { text: string; altInitialMaxHeight?: number },
) {
    const expanded = useSignal(false)
    const id = 'descricao-produto'
    return (
        <div class='flex flex-col gap-2'>
            <div class='flex items-center justify-between'>
                <h2>
                    <strong class='font-semibold text-sm text-neutral-1'>
                        Descrição do Produto
                    </strong>
                </h2>
            </div>
            <div
                class={`relative transition-all duration-300 ${
                    expanded.value ? '' : 'overflow-hidden'
                }`}
                style={!expanded.value ? { maxHeight: `${altInitialMaxHeight}px` } : undefined}
            >
                <RenderHTML
                    id={id}
                    html={text}
                    class='prose font-tt-norms text-neutral-1 leading-tight text-sm'
                />
                {!expanded.value && (
                    <div class='pointer-events-none absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-white via-white/60 to-transparent' />
                )}
            </div>
            <button
                type='button'
                onClick={() => {
                    const newVal = !expanded.value
                    expanded.value = newVal
                    // Notify any client-side controller (island) to toggle sticky image
                    if (typeof globalThis !== 'undefined') {
                        globalThis.dispatchEvent(
                            new CustomEvent('product-description-toggle', {
                                detail: { expanded: newVal },
                            }),
                        )
                    }
                }}
                aria-expanded={expanded.value}
                class='inline-flex items-center gap-1 self-start mt-[-7.5px] font-lato font-semibold text-sm leading-4 text-[#FF5101]'
            >
                <span class='underline'>{expanded.value ? 'Ver menos' : 'Ver mais'}</span>
                <Icon
                    id={expanded.value ? 'DescChevronUp' : 'DescChevronDown'}
                    width={13}
                    height={7}
                    class='text-[#FF5101] relative top-[1px]'
                />
            </button>
        </div>
    )
}

function Description({
    text,
    variant = 'default',
    altInitialMaxHeight,
}: Props) {
    if (variant === 'alt') {
        return <AltVariant text={text} altInitialMaxHeight={altInitialMaxHeight} />
    }
    return <DefaultVariant text={text} />
}

export default Description
